<!--
  ============================================================================
  MaltaHealthLanding — Landing page do evento "Malta Health Innovation Circle"
  (SO/check · wellbeing preventive platform).

  Estrutura:
    1. Hero  : Logo + Headline + Subheadline + VSL + CTA primário
    2. Topics: "What Will We Explore?" — lista com checks
    3. Experts: Dr. Francisco Mera + Dr. Luis F. Jaime
    4. Schedule: "Event Experience" — timeline de horários
    5. Who Should Attend: chips de perfis profissionais
    6. CTA final: "Request Your Invitation" (limitado a 30)

  Paleta (premium · médica · inovadora):
    - Navy profundo            #02142A
    - Azul Oligocheck          #009FE3
    - Azul ciano luminoso      #45D5FF
    - Branco
    - Glow azul neon + degradês tecnológicos sutis

  Idioma: EN. CTA → WhatsApp.
  ============================================================================
-->
<template>
  <AppShell
    v-model:current-lang="currentLang"
    variante="produto"
    class="malta-page"
    :mostrar-switcher="false"
    :auto-idioma="false"
  >
    <!-- ============================================================
         1. HERO + VÍDEO
            Logo + Headline + Subheadline + VSL + CTA primário.
         ============================================================ -->
    <header class="hero">
      <div class="hero-glow" aria-hidden="true"></div>
      <div class="hero-dna" aria-hidden="true"></div>

      <div class="hero-inner">
        <div class="hero-logo">
          <img :src="logoBranco" alt="SO/check" />
        </div>

        <h1 class="hero-titulo">
          <span class="t-line t-white">MALTA</span>
          <span class="t-line ds-texto-gradiente">HEALTH</span>
          <span class="t-line t-white">INNOVATION</span>
          <span class="t-circle">— C I R C L E —</span>
        </h1>

        <span class="hero-data">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          12 September, Hotel Verdala
        </span>

        <p class="hero-lead">
          The Future of
          <span class="lead-accent">Preventive Health</span>
          in Just <span class="lead-accent">5 Seconds</span>
        </p>

        <!-- VÍDEO VSL no topo da página -->
        <div class="video-wrapper" aria-label="Malta Health Innovation Circle — event video">
          <iframe
            :src="videoEmbedUrl"
            title="Malta Health Innovation Circle"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
            loading="lazy"
          ></iframe>
        </div>

        <!-- CTA principal → formulário no final da página -->
        <a href="#apply" class="hero-cta">
          Request your invitation
          <svg class="hero-cta-seta" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
          </svg>
        </a>
      </div>
    </header>

    <!-- ============================================================
         2. WHAT WILL WE EXPLORE?
            Lista com checks — temas centrais do evento.
         ============================================================ -->
    <SecaoPadrao id="explore" variante="alt">
      <SecaoHeader
        titulo-antes="What Will We"
        titulo-destaque="Explore?"
        subtitulo="A morning dedicated to innovation, preventive health and wellbeing"
      />

      <p class="explore-label">Topics include:</p>

      <ul class="explore-list">
        <li v-for="(item, i) in topicos" :key="i" class="explore-item" :class="{ 'explore-item--highlight': item.destaque }">
          <span class="explore-check" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </span>
          <span class="explore-texto">{{ item.texto }}</span>
        </li>
      </ul>
    </SecaoPadrao>

    <!-- ============================================================
         3. INVITED EXPERTS
            Cards dos especialistas convidados.
         ============================================================ -->
    <SecaoPadrao id="experts">
      <SecaoHeader
        titulo-antes="Invited"
        titulo-destaque="Experts"
      />

      <div class="experts-grid">
        <article v-for="expert in experts" :key="expert.nome" class="expert-card">
          <div class="expert-avatar">
            <div class="expert-avatar-ring" aria-hidden="true"></div>
            <img :src="expert.foto" :alt="expert.nome" loading="lazy" />
          </div>
          <h3 class="expert-nome">{{ expert.nome }}</h3>
          <p v-if="expert.titulo" class="expert-titulo">{{ expert.titulo }}</p>
          <ul v-if="expert.credenciais.length" class="expert-credenciais">
            <li v-for="(c, i) in expert.credenciais" :key="i">{{ c }}</li>
          </ul>
        </article>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         4. EVENT EXPERIENCE — timeline horária
         ============================================================ -->
    <SecaoPadrao id="schedule" variante="alt">
      <SecaoHeader
        titulo-antes="Event"
        titulo-destaque="Experience"
      />

      <ol class="schedule">
        <li v-for="(slot, i) in agenda" :key="i" class="schedule-item" :class="{ 'schedule-item--demo': slot.demo }">
          <span class="schedule-time">{{ slot.hora }}</span>
          <span class="schedule-marker" aria-hidden="true"></span>
          <div class="schedule-content">
            <h3 class="schedule-titulo">{{ slot.titulo }}</h3>
            <p v-if="slot.autor" class="schedule-autor">{{ slot.autor }}</p>
          </div>
        </li>
      </ol>
    </SecaoPadrao>

    <!-- ============================================================
         5. WHO SHOULD ATTEND?
         ============================================================ -->
    <SecaoPadrao id="who">
      <SecaoHeader
        titulo-antes="Who Should"
        titulo-destaque="Attend?"
      />

      <div class="perfil-chips">
        <span v-for="p in perfis" :key="p" class="perfil-chip">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          {{ p }}
        </span>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         6. CTA FINAL → WhatsApp (Request Your Invitation)
         ============================================================ -->
    <SecaoPadrao id="apply" variante="alt" class="secao-cta-final">
      <div class="cta-final">
        <h2 class="cta-final-titulo">
          Request Your <span class="ds-texto-gradiente">Invitation</span>
        </h2>
        <p class="cta-final-desc">
          The Malta Health Innovation Circle has been intentionally limited to
          <strong>30 professionals</strong> to ensure a high-quality and
          interactive experience. Submit your application below to be considered
          for attendance.
        </p>
      </div>

      <div class="cta-final-form">
        <FormularioLead
          idioma="en"
          pais-padrao="MT"
          :active-campaign="acConfig"
          :redirect-to="{ name: 'malta-gracias' }"
          :mostrar-header="false"
          :profissoes="profissoesFormulario"
          texto-botao="Request your invitation"
          @submitted="onLeadSubmitted"
        />
      </div>
    </SecaoPadrao>

    <template #rodape>
      <RodapeSite
        marca="SO/check"
        direitos="All rights reserved."
      />
    </template>
  </AppShell>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import AppShell from '@/components/global/layout/AppShell.vue'
import SecaoPadrao from '@/components/global/secao/SecaoPadrao.vue'
import SecaoHeader from '@/components/global/secao/SecaoHeader.vue'
import RodapeSite from '@/components/global/layout/RodapeSite.vue'
import FormularioLead from '@/components/global/formulario/FormularioLead.vue'
import { sendLeadToWebhook } from '@/lib/webhookLead'
import logoBranco from '@/assets/images/logo-branco.svg'
import drFranciscoMera from '@/assets/images/malta-health/dr-francisco-mera.png'
import drLuisJaime from '@/assets/images/malta-health/dr-luis-jaime.png'

/* Idioma fixo: o evento é apresentado em inglês (conteúdo do flyer). */
const currentLang = ref('en')

/* Título da aba — esta página não usa useSeo, então garantimos o
   <title> correto ao montar (evita herdar o título padrão do index.html). */
onMounted(() => {
  document.title = 'Malta Health Innovation Circle'
})

/* ============================================================
   VÍDEO DO EVENTO (VSL)
   URL fornecida: https://youtu.be/tRlLYGvOE9Y
   ============================================================ */
const VIDEO_ID = 'tRlLYGvOE9Y'
const videoEmbedUrl = `https://www.youtube.com/embed/${VIDEO_ID}?rel=0&controls=1`

/* ============================================================
   FORMULÁRIO DE CAPTURA
   ------------------------------------------------------------
   ActiveCampaign — form dedicado do evento Malta (form_45).
   Campos custom: field[1] = profissão · field[2] = país.
   ============================================================ */
const acConfig = {
  endpoint: 'https://eduardgasset95711.activehosted.com/proc.php',
  u: '45',
  f: '45',
  s: '',
  c: '0',
  m: '0',
  act: 'sub',
  v: '2',
  or: 'e9504938-7768-4205-b527-6252971ad56c'
}

/* Opções de profissão do formulário do evento Malta. */
const profissoesFormulario = [
  'Doctor',
  'Nutritionist',
  'Wellness clinic',
  'Other healthcare professional'
]

/* Ao submeter: envia o lead ao webhook do Make (Meta CAPI server-side).
   O redirect para a página de obrigado é feito pelo próprio FormularioLead. */
const onLeadSubmitted = (payload) => {
  sendLeadToWebhook(payload, {
    contentName: 'malta_health_circle',
    contentCategory: 'evento_malta',
    currency: 'EUR'
  })
}

/* ============================================================
   CONTEÚDO — extraído diretamente do briefing.
   ============================================================ */
const topicos = [
  { texto: 'The role of trace elements in health and wellbeing' },
  { texto: 'Heavy metals and environmental exposure' },
  { texto: 'Oxidative stress and healthy aging' },
  { texto: 'Preventive health strategies' },
  { texto: 'Wellness optimisation' },
  { texto: 'Innovative technologies in healthcare' },
  { texto: 'LIVE OLIGOCHECK DEMONSTRATION', destaque: true },
  { texto: 'Real clinical applications and case discussions' }
]

const experts = [
  {
    nome: 'Dr. Francisco Mera',
    titulo: 'Longevity & Precision Medicine',
    foto: drFranciscoMera,
    credenciais: [
      'Doctor and researcher specializing in longevity, precision medicine, and preventive health'
    ]
  },
  {
    nome: 'Dr. Luis F. Jaime MD PhD',
    titulo: 'Longevity & Regenerative Medicine',
    foto: drLuisJaime,
    credenciais: [
      'Anesthesiologist',
      'CEO & Founder The Longevity Experts Group'
    ]
  }
]

const agenda = [
  { hora: '09:00', titulo: 'Welcome Coffee & Networking' },
  { hora: '10:00', titulo: 'Beyond Symptoms: The New Era of Preventive Medicine', autor: 'Dr Francisco Mera' },
  { hora: '11:00', titulo: 'Coffee Break & Networking' },
  { hora: '11:30', titulo: 'Clinical Insights: From Data to Personalized Interventions', autor: 'Dr Luis Jaime' },
  { hora: '12:15', titulo: 'Live Demonstration Oligocheck', demo: true },
  { hora: '13:15', titulo: 'Final Reflections' }
]

const perfis = [
  'Healthcare Professionals',
  'Functional Medicine Practitioners',
  'Nutritionists',
  'Longevity Professionals',
  'Wellness Clinics',
  'Integrative Health Professionals'
]
</script>

<style scoped>
/* ============================================================
   PALETA LOCAL — Oligocheck deep navy + cyan brand
     · Navy profundo            #02142A
     · Azul Oligocheck          #009FE3
     · Azul ciano luminoso      #45D5FF
     · Branco
   Custom properties cascateiam para badges, FeatureCard,
   gradiente de texto, rodapé etc.
   ============================================================ */
.app-shell--produto {
  --ds-accent: #009FE3;
  --ds-accent-2: #45D5FF;
  --ds-accent-soft: rgba(0, 159, 227, 0.12);
  --ds-accent-border: rgba(69, 213, 255, 0.32);
  --ds-ciano: #45D5FF;
  --ds-ciano-soft: rgba(69, 213, 255, 0.12);
  --ds-gradient-text: linear-gradient(90deg, #45D5FF, #009FE3, #45D5FF);
}

/* ============================================================
   HERO
   ============================================================ */
.hero {
  position: relative;
  overflow: hidden;
  padding: 56px var(--ds-s-5) var(--ds-s-11);
  background:
    radial-gradient(ellipse 900px 700px at 75% 12%, rgba(0, 159, 227, 0.22) 0%, transparent 55%),
    radial-gradient(ellipse 700px 500px at 12% 80%, rgba(69, 213, 255, 0.10) 0%, transparent 60%),
    linear-gradient(180deg, #02142A 0%, #010d18 60%);
}

.hero-glow {
  position: absolute;
  top: -120px;
  right: -120px;
  width: 520px;
  height: 520px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 159, 227, 0.32) 0%, transparent 65%);
  filter: blur(20px);
  pointer-events: none;
  z-index: 0;
}

.hero-dna {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 80% 30%, rgba(69, 213, 255, 0.12) 0, transparent 2px),
    radial-gradient(circle at 88% 55%, rgba(69, 213, 255, 0.10) 0, transparent 2px),
    radial-gradient(circle at 72% 70%, rgba(69, 213, 255, 0.10) 0, transparent 2px);
  background-size: 140px 140px;
  opacity: 0.6;
  pointer-events: none;
  z-index: 0;
}

.hero-inner {
  position: relative;
  z-index: 1;
  max-width: 860px;
  margin: 0 auto;
  text-align: center;
  animation: heroFadeInUp 0.8s ease-out;
}

.hero-logo {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-bottom: var(--ds-s-6);
}

.hero-logo img {
  height: 27px;
}

/* Título empilhado estilo flyer */
.hero-titulo {
  font-family: var(--ds-font-display);
  margin: 0 0 var(--ds-s-6);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  line-height: 0.98;
  letter-spacing: 1px;
}

.t-line {
  font-size: clamp(32px, 8.8vw, 69px);
  text-transform: uppercase;
}

.t-white { color: var(--ds-text); }

.t-circle {
  font-size: clamp(11px, 2.7vw, 18px);
  letter-spacing: 6px;
  color: var(--ds-text-muted);
  margin-top: 6px;
}

.hero-data {
  display: inline-flex;
  align-items: center;
  gap: var(--ds-s-2);
  margin: var(--ds-s-5) 0 var(--ds-s-3);
  padding: 8px var(--ds-s-4);
  border-radius: var(--ds-r-full);
  font-size: 14px;
  font-family: var(--ds-font-bold);
  letter-spacing: 0.5px;
  background: var(--ds-accent-soft);
  border: 1px solid var(--ds-accent-border);
  color: var(--ds-accent-2);
}

.hero-data svg { flex-shrink: 0; }

.hero-lead {
  font-family: var(--ds-font-bold);
  font-size: clamp(20px, 3.4vw, 28px);
  color: var(--ds-text);
  margin: 0 0 var(--ds-s-8);
  line-height: 1.25;
}

.lead-accent {
  background: var(--ds-gradient-text);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* ============================================================
   VÍDEO VSL
   ============================================================ */
.video-wrapper {
  position: relative;
  width: 100%;
  max-width: 760px;
  aspect-ratio: 16 / 9;
  margin: 0 auto var(--ds-s-8);
  border-radius: var(--ds-r-xl);
  overflow: hidden;
  background: #000;
  border: 1px solid var(--ds-accent-border);
  box-shadow:
    0 30px 80px -20px rgba(0, 0, 0, 0.7),
    0 0 0 1px rgba(0, 159, 227, 0.14),
    0 0 60px -10px rgba(69, 213, 255, 0.38);
}

.video-wrapper iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

/* ============================================================
   CTA (hero + final) — botão WhatsApp
   ============================================================ */
.hero-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--ds-s-3);
  font-family: var(--ds-font-bold);
  font-size: 16px;
  padding: 16px 28px;
  border-radius: var(--ds-r-full);
  text-decoration: none;
  color: #fff;
  background: linear-gradient(135deg, #45D5FF 0%, #009FE3 60%, #0078b8 100%);
  box-shadow: 0 10px 28px -6px rgba(0, 159, 227, 0.55);
  transition: all var(--ds-t-base);
}

.hero-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 36px -8px rgba(69, 213, 255, 0.75);
}

.hero-cta-icone { display: inline-flex; }
.hero-cta-seta { transition: transform var(--ds-t-base); }
.hero-cta:hover .hero-cta-seta { transform: translateX(4px); }

/* ============================================================
   EXPLORE — lista com checks (What Will We Explore?)
   ============================================================ */
.explore-label {
  max-width: 880px;
  margin: 0 auto var(--ds-s-4);
  font-family: var(--ds-font-bold);
  font-size: 15px;
  color: var(--ds-text);
}

.explore-list {
  list-style: none;
  margin: 0 auto;
  padding: 0;
  max-width: 880px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--ds-s-3);
}

.explore-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  background: var(--ds-surface-1);
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-r-md);
  transition: all var(--ds-t-base);
}

.explore-item:hover {
  border-color: var(--ds-accent-border);
  background: var(--ds-surface-3);
  transform: translateY(-2px);
}

.explore-item--highlight {
  background: linear-gradient(135deg, rgba(0, 159, 227, 0.16), rgba(69, 213, 255, 0.08));
  border-color: rgba(69, 213, 255, 0.45);
  box-shadow: 0 0 0 1px rgba(69, 213, 255, 0.18), 0 8px 28px -10px rgba(0, 159, 227, 0.4);
}

.explore-item--highlight .explore-texto {
  font-family: var(--ds-font-bold);
  color: var(--ds-text);
  letter-spacing: 0.5px;
}

.explore-check {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--ds-accent-soft);
  color: var(--ds-accent-2);
  border: 1px solid var(--ds-accent-border);
}

.explore-item--highlight .explore-check {
  background: var(--ds-accent);
  color: #fff;
  border-color: var(--ds-accent);
  box-shadow: 0 0 16px rgba(69, 213, 255, 0.55);
}

.explore-texto {
  font-size: 15px;
  color: var(--ds-text-muted);
  line-height: 1.45;
}

/* ============================================================
   EXPERTS — cards
   ============================================================ */
.experts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--ds-s-5);
  max-width: 880px;
  margin: 0 auto;
}

.expert-card {
  position: relative;
  background: var(--ds-surface-1);
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-r-lg);
  padding: 32px 28px;
  text-align: center;
  transition: all var(--ds-t-base);
  overflow: hidden;
}

.expert-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60%;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--ds-accent), transparent);
  opacity: 0.5;
}

.expert-card:hover {
  transform: translateY(-4px);
  border-color: var(--ds-accent-border);
  background: var(--ds-surface-3);
  box-shadow: 0 16px 40px -16px rgba(0, 159, 227, 0.35);
}

.expert-avatar {
  position: relative;
  width: 128px;
  height: 128px;
  border-radius: 50%;
  margin: 0 auto var(--ds-s-5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: linear-gradient(135deg, #45D5FF 0%, #009FE3 60%, #02142A 100%);
  box-shadow:
    0 12px 32px -10px rgba(0, 159, 227, 0.55),
    0 0 0 1px rgba(69, 213, 255, 0.18),
    0 0 28px -4px rgba(69, 213, 255, 0.35);
  transition: all var(--ds-t-base);
}

.expert-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  object-position: center top;
  background: var(--ds-surface-2);
  display: block;
}

.expert-avatar-ring {
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  border: 1px solid rgba(69, 213, 255, 0.25);
  pointer-events: none;
  opacity: 0.8;
  transition: all var(--ds-t-base);
}

.expert-card:hover .expert-avatar {
  transform: scale(1.04);
  box-shadow:
    0 18px 42px -10px rgba(0, 159, 227, 0.7),
    0 0 0 1px rgba(69, 213, 255, 0.28),
    0 0 38px -2px rgba(69, 213, 255, 0.55);
}

.expert-card:hover .expert-avatar-ring {
  inset: -10px;
  opacity: 1;
  border-color: rgba(69, 213, 255, 0.45);
}

.expert-nome {
  font-family: var(--ds-font-bold);
  font-size: 20px;
  color: var(--ds-text);
  margin: 0 0 6px;
}

.expert-titulo {
  font-size: 13px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--ds-accent-2);
  margin: 0 0 var(--ds-s-4);
  font-family: var(--ds-font-bold);
}

.expert-credenciais {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.expert-credenciais li {
  font-size: 14px;
  color: var(--ds-text-muted);
  line-height: 1.45;
}

/* ============================================================
   SCHEDULE — timeline horária
   ============================================================ */
.schedule {
  list-style: none;
  margin: 0 auto;
  padding: 0;
  max-width: 720px;
  position: relative;
}

.schedule::before {
  content: '';
  position: absolute;
  top: 12px;
  bottom: 12px;
  left: 92px;
  width: 2px;
  background: linear-gradient(180deg, var(--ds-accent), rgba(69, 213, 255, 0.2));
}

.schedule-item {
  position: relative;
  display: grid;
  grid-template-columns: 80px 24px 1fr;
  gap: 12px;
  align-items: flex-start;
  padding: var(--ds-s-3) 0;
}

.schedule-item + .schedule-item { padding-top: var(--ds-s-4); }

.schedule-time {
  font-family: var(--ds-font-bold);
  font-size: 15px;
  line-height: 1.4;
  color: var(--ds-accent-2);
  letter-spacing: 0.5px;
  text-align: right;
  padding-top: 0;
}

.schedule-marker {
  position: relative;
  width: 14px;
  height: 14px;
  margin-top: 3px;
  margin-left: 5px;
  border-radius: 50%;
  background: var(--ds-bg);
  border: 2px solid var(--ds-accent);
  box-shadow: 0 0 0 4px rgba(0, 159, 227, 0.12);
  z-index: 1;
}

.schedule-item--demo .schedule-marker {
  background: var(--ds-accent);
  box-shadow: 0 0 0 4px rgba(69, 213, 255, 0.22), 0 0 18px rgba(69, 213, 255, 0.6);
}

.schedule-content {
  padding: 4px 0;
}

.schedule-titulo {
  font-family: var(--ds-font-bold);
  font-size: 16px;
  color: var(--ds-text);
  margin: 0 0 2px;
  line-height: 1.4;
}

.schedule-autor {
  margin: 0;
  font-size: 14px;
  color: var(--ds-text-muted);
}

/* ============================================================
   PERFIL — chips
   ============================================================ */
.perfil-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--ds-s-3);
  max-width: 820px;
  margin: 0 auto;
}

.perfil-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px var(--ds-s-5);
  background: var(--ds-surface-1);
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-r-full);
  font-size: 14px;
  color: var(--ds-text-muted);
  transition: all var(--ds-t-base);
}

.perfil-chip:hover {
  border-color: var(--ds-accent-border);
  color: var(--ds-text);
  background: var(--ds-surface-3);
}

.perfil-chip svg { color: var(--ds-accent); flex-shrink: 0; }

/* ============================================================
   CTA FINAL
   ============================================================ */
.secao-cta-final { text-align: center; }

.cta-final {
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.cta-final-titulo {
  font-family: var(--ds-font-display);
  font-size: clamp(26px, 5vw, 38px);
  line-height: 1.2;
  margin: 0 0 var(--ds-s-4);
  color: var(--ds-text);
}

.cta-final-desc {
  font-size: 16px;
  color: var(--ds-text-muted);
  line-height: 1.6;
  margin: 0 0 var(--ds-s-6);
  max-width: 600px;
}

.cta-final-desc strong { color: var(--ds-accent-2); font-family: var(--ds-font-bold); }

.cta-final-form {
  margin-top: var(--ds-s-7);
}

/* ============================================================
   RESPONSIVO
   ============================================================ */
@media (max-width: 720px) {
  .hero { padding: 40px var(--ds-s-4) var(--ds-s-10); }
  .video-wrapper { border-radius: var(--ds-r-lg); margin-bottom: var(--ds-s-6); }
  .hero-cta { width: 100%; font-size: 15px; }

  .schedule::before { left: 70px; }
  .schedule-item { grid-template-columns: 60px 20px 1fr; gap: 10px; }
  .schedule-time { font-size: 13px; }
}

@keyframes heroFadeInUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
